import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

const GridHero = () => {
  const tiles = [
    {
      id: "marchi",
      title: "MARCHI",
      subtitle: "Storia e Prestigio",
      cta: "SCOPRI",
      image: "/lovable-uploads/panorama-toscana-hero.png",
      link: "/marchi"
    },
    {
      id: "fabbriche", 
      title: "FABBRICHE",
      subtitle: "Alta Qualità Artigianale",
      cta: "VISITA",
      image: "/lovable-uploads/distilleria-hero-carousel.png",
      link: "/fabbriche"
    },
    {
      id: "emporio",
      title: "SHOP", 
      subtitle: "Prodotti di Eccellenza",
      cta: "ASSAPORA",
      image: "/lovable-uploads/famiglia-prodotti-hero.png",
      link: "https://www.fonderiadelcacao.it/shop"
    },
    {
      id: "progetti",
      title: "PROGETTI",
      subtitle: "Soluzioni per Ogni Esigenza", 
      cta: "CREA",
      image: "/lovable-uploads/PROGETTI.png",
      link: "/progetti"
    }
  ];

  const autoplayPlugin = Autoplay({ delay: 5000, stopOnInteraction: true });
  
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "center"
    },
    [autoplayPlugin]
  );

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const toggleAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    const playOrStop = autoplay.isPlaying() ? autoplay.stop : autoplay.play;
    playOrStop();
    setIsPlaying(!autoplay.isPlaying());
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section 
      className="relative w-full h-[calc(100dvh-var(--header-h)-var(--bottom-nav-h))] lg:h-[calc(100dvh-var(--header-h))]"
      aria-label="Slider principale con contenuti in evidenza"
    >
      {/* Navigation Controls */}
      <div className="absolute top-1/2 -translate-y-1/2 z-20 left-4 right-4 flex justify-between pointer-events-none">
        <Button
          variant="outline"
          size="icon"
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          className="pointer-events-auto h-12 w-12 bg-black/20 border-white/40 text-white hover:bg-black/40 hover:border-white hover:text-white hover:scale-110 backdrop-blur-sm transition-all duration-300"
          aria-label="Slide precedente"
        >
          <ChevronLeft className="h-6 w-6" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={scrollNext}
          disabled={!canScrollNext}
          className="pointer-events-auto h-12 w-12 bg-black/20 border-white/40 text-white hover:bg-black/40 hover:border-white hover:text-white hover:scale-110 backdrop-blur-sm transition-all duration-300"
          aria-label="Slide successiva"
        >
          <ChevronRight className="h-6 w-6" />
        </Button>
      </div>

      {/* Autoplay Control */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          variant="outline"
          size="icon"
          onClick={toggleAutoplay}
          className="h-10 w-10 bg-black/20 border-white/40 text-white hover:bg-black/40 hover:border-white hover:text-white backdrop-blur-sm transition-all duration-300"
          aria-label={isPlaying ? "Metti in pausa" : "Riproduci"}
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </Button>
      </div>

      {/* Carousel */}
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {tiles.map((tile, tileIndex) => {
            const isExternal = tile.link.startsWith("http");
            const slideContent = (
              <>
                <div className="absolute inset-0">
                  <img
                    src={tile.image}
                    alt={`${tile.title} - ${tile.subtitle}`}
                    width={1920}
                    height={1080}
                    className="w-full h-full object-cover transition-transform duration-[1000ms] ease-out group-hover:scale-105"
                    loading={tileIndex === 0 ? "eager" : "lazy"}
                    fetchPriority={tileIndex === 0 ? "high" : "auto"}
                    decoding={tileIndex === 0 ? "sync" : "async"}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20 group-hover:from-black/30 group-hover:via-black/10 group-hover:to-transparent transition-all duration-700"></div>
                </div>
                <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-3 py-2 sm:px-6 sm:py-4 md:px-8 md:py-8">
                  <div className="flex flex-col items-center space-y-2 sm:space-y-3 md:space-y-4 max-w-full animate-fade-in">
                    <h2 className="font-display text-display-xl tablet-md:text-display-2xl text-primary-foreground drop-shadow-2xl shadow-black/80 tracking-wider transform transition-all duration-700 group-hover:scale-110 group-hover:tracking-widest text-center leading-tight max-w-[90%] sm:max-w-full gpu-accelerated" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)'}}>
                      {tile.title}
                    </h2>
                    <p className="font-display text-body-lg tablet-md:text-body-xl text-primary-foreground drop-shadow-2xl shadow-black/80 tracking-wide italic transform transition-all duration-700 group-hover:scale-105 group-hover:opacity-90 text-center leading-snug max-w-[85%] sm:max-w-sm md:max-w-md gpu-accelerated" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.6)'}}>
                      {tile.subtitle}
                    </p>
                    <div className="border-2 border-primary-foreground/80 px-6 py-3 sm:px-8 sm:py-4 bg-black/10 backdrop-blur-sm mt-3 sm:mt-4 shadow-lg group-hover:bg-white/20 group-hover:border-white group-hover:shadow-2xl transition-all duration-500 gpu-accelerated">
                      <p className="font-display text-body-sm tablet-md:text-body-md text-primary-foreground drop-shadow-lg shadow-black/60 tracking-[0.15em] font-semibold transform transition-all duration-500 group-hover:scale-110 group-hover:tracking-[0.2em] uppercase" style={{textShadow: '1px 1px 2px rgba(0,0,0,0.6)'}}>
                        {tile.cta}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            );
            const className = "group relative overflow-hidden transition-all duration-500 hover:brightness-110 focus:brightness-110 focus:outline-none focus-ring block w-full h-full";
            return (
              <div key={tile.id} className="flex-[0_0_100%] min-w-0 h-full">
                {isExternal ? (
                  <a href={tile.link} target="_blank" rel="noopener noreferrer" className={className} aria-label={`${tile.title}: ${tile.subtitle}`}>
                    {slideContent}
                  </a>
                ) : (
                  <Link to={tile.link} className={className} aria-label={`${tile.title}: ${tile.subtitle}`}>
                    {slideContent}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 lg:bottom-8 left-1/2 -translate-x-1/2 z-20">
        <div className="flex gap-2 lg:gap-4 bg-black/20 backdrop-blur-sm px-4 py-2 rounded-full">
          {tiles.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className="relative w-3 h-3 p-0 m-0 border-0 bg-transparent lg:w-auto lg:h-auto group"
              aria-label={`Vai alla slide ${index + 1}`}
            >
              <span
                className={`lg:hidden absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 block rounded-full transition-all duration-500 ${
                  index === selectedIndex 
                    ? 'w-[8px] h-[8px] bg-white shadow-lg' 
                    : 'w-[4px] h-[4px] bg-white/50 group-hover:bg-white/80 group-hover:w-[6px] group-hover:h-[6px]'
                }`}
              />
              <span
                className={`hidden lg:block rounded-full transition-all duration-500 ${
                  index === selectedIndex 
                    ? 'w-4 h-4 bg-white shadow-lg scale-125' 
                    : 'w-3 h-3 bg-white/50 hover:bg-white/80 hover:scale-110'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GridHero;